import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Activity, Flame, Target, Loader2, Sparkles } from "lucide-react";
import api, { getErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Auth() {
  const { loginSuccess } = useAuth();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [plan, setPlan] = useState(null);

  useEffect(() => {
    api.get("/payments/plan").then((r) => setPlan(r.data)).catch(() => {});
  }, []);

  const CUR = { gbp: "£", usd: "$", eur: "€" };
  const priceLabel = plan
    ? `${CUR[plan.currency] || ""}${(plan.amount / 100).toFixed(2)}/${plan.interval}`
    : "£9.99/year";

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const path = mode === "login" ? "/auth/login" : "/auth/register";
      const payload = mode === "login" ? { email, password } : { email, password, name };
      const { data } = await api.post(path, payload);
      loginSuccess(data);
      toast.success(mode === "login" ? "Welcome back!" : "Account created!");
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const requestReset = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      toast.success("If that email exists, a code is on its way.");
      setMode("reset");
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const doReset = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { email, code, new_password: password });
      toast.success("Password updated! Please sign in.");
      setMode("login");
      setCode("");
      setPassword("");
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const HEADINGS = {
    login: ["Welcome back", "Sign in to keep your streak alive."],
    register: ["Create your account", "Start tracking your habits today."],
    forgot: ["Reset your password", "Enter your email and we'll send a 6-digit code."],
    reset: ["Enter your code", "Check your inbox for the 6-digit code, then set a new password."],
  };
  const onSubmit = mode === "forgot" ? requestReset : mode === "reset" ? doReset : submit;

  return (
    <div className="min-h-screen grain flex flex-col lg:flex-row">
      {/* Left brand panel */}
      <div className="relative hidden lg:flex flex-col justify-between w-1/2 p-12 overflow-hidden bg-[#0D0F17] border-r border-[#242417]">
        <div className="absolute -top-20 -left-20 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl" />
        <div className="relative flex items-center gap-3">
          <img src="/logo.png" alt="SMART Momentum" className="w-11 h-11 rounded-xl glow-emerald" />
          <span className="font-display text-2xl font-extrabold tracking-tight" data-testid="brand-wordmark-hero">
            <span className="text-emerald-400">SMART</span> Momentum
          </span>
        </div>
        <div className="relative space-y-6">
          <span data-testid="hero-trial-badge" className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 text-sm font-semibold">
            <Sparkles className="w-4 h-4" /> 7-day free trial · no card needed
          </span>
          <h1 className="font-display text-5xl font-black leading-tight tracking-tight">
            Build habits that <span className="text-emerald-400">actually stick</span>.
          </h1>
          <p className="text-white text-lg max-w-md">
            Track daily check-offs, countable goals and watch your streaks catch fire. Your command center for consistency.
          </p>
          <div data-testid="smart-acronym" className="max-w-md p-4 rounded-2xl bg-emerald-500/10 border border-emerald-500/30">
            <p className="m-0 font-semibold whitespace-nowrap" style={{ fontSize: "clamp(11px, 3.4vw, 14px)" }}>
              <span className="text-emerald-400 font-black">S</span>mall{" "}
              <span className="text-emerald-400 font-black">M</span>eaningful{" "}
              <span className="text-emerald-400 font-black">A</span>ctions{" "}
              <span className="text-emerald-400 font-black">R</span>epeated{" "}
              <span className="text-emerald-400 font-black">T</span>oday
            </p>
            <p className="m-0 mt-1 text-sm text-white">= Momentum</p>
          </div>
          <p data-testid="hero-price-line" className="text-white text-sm max-w-md">
            Start with a <span className="text-white font-semibold">7-day free trial</span>, then just{" "}
            <span className="text-emerald-400 font-bold">{priceLabel}</span>. Cancel anytime.
          </p>
          <div className="flex gap-8 pt-4">
            <Feature icon={<Target className="w-5 h-5 text-emerald-400" />} label="Daily Goals" />
            <Feature icon={<Flame className="w-5 h-5 text-orange-500" />} label="Streaks" />
            <Feature icon={<Activity className="w-5 h-5 text-blue-400" />} label="Analytics" />
          </div>
        </div>
        <div className="relative text-white text-sm">Small Meaningful Actions Repeated Today = Momentum</div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-md"
        >
          <div className="lg:hidden mb-8" data-testid="mobile-hero">
            <div className="flex items-center gap-3 mb-6">
              <img src="/logo.png" alt="SMART Momentum" className="w-10 h-10 rounded-xl glow-emerald" />
              <span className="font-display text-xl font-extrabold" data-testid="brand-wordmark-mobile">
                <span className="text-emerald-400">SMART</span> Momentum
              </span>
            </div>
            <h1 className="font-display text-3xl sm:text-4xl font-black leading-tight tracking-tight mb-3">
              Build habits that <span className="text-emerald-400">actually stick</span>.
            </h1>
            <p className="text-white text-base mb-4">
              Track daily check-offs, countable goals and watch your streaks catch fire.
            </p>
            <div className="p-4 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 mb-4">
              <p className="m-0 font-semibold whitespace-nowrap" style={{ fontSize: "clamp(11px, 3.6vw, 15px)" }}>
                <span className="text-emerald-400 font-black">S</span>mall{" "}
                <span className="text-emerald-400 font-black">M</span>eaningful{" "}
                <span className="text-emerald-400 font-black">A</span>ctions{" "}
                <span className="text-emerald-400 font-black">R</span>epeated{" "}
                <span className="text-emerald-400 font-black">T</span>oday
              </p>
              <p className="m-0 mt-1 text-sm text-white">= Momentum</p>
            </div>
            <div className="flex gap-6">
              <Feature icon={<Target className="w-5 h-5 text-emerald-400" />} label="Daily Goals" />
              <Feature icon={<Flame className="w-5 h-5 text-orange-500" />} label="Streaks" />
              <Feature icon={<Activity className="w-5 h-5 text-blue-400" />} label="Analytics" />
            </div>
          </div>

          {(mode === "login" || mode === "register") && (
            <div data-testid="form-trial-badge" className="inline-flex items-center gap-2 px-3 py-1.5 mb-4 rounded-full bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 text-sm font-semibold">
              <Sparkles className="w-4 h-4" /> 7-day free trial, then {priceLabel}
            </div>
          )}

          <h2 className="font-display text-3xl font-extrabold tracking-tight mb-1">
            {HEADINGS[mode][0]}
          </h2>
          <p className="text-white mb-8">{HEADINGS[mode][1]}</p>

          <form onSubmit={onSubmit} className="space-y-4">
            {mode === "register" && (
              <Field label="Name">
                <input
                  data-testid="register-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  className="input-field"
                />
              </Field>
            )}

            {(mode === "login" || mode === "register" || mode === "forgot") && (
              <Field label="Email">
                <input
                  data-testid="login-email-input"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="input-field"
                />
              </Field>
            )}

            {mode === "reset" && (
              <Field label="6-digit code">
                <input
                  data-testid="reset-code-input"
                  inputMode="numeric"
                  maxLength={6}
                  required
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  placeholder="123456"
                  className="input-field tracking-[0.5em] text-center font-mono-stat"
                />
              </Field>
            )}

            {(mode === "login" || mode === "register" || mode === "reset") && (
              <Field label={mode === "reset" ? "New password" : "Password"}>
                <input
                  data-testid="login-password-input"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="input-field"
                />
              </Field>
            )}

            {mode === "login" && (
              <div className="text-right -mt-1">
                <button
                  type="button"
                  data-testid="forgot-password-link"
                  onClick={() => { setMode("forgot"); setError(""); }}
                  className="text-sm text-emerald-400 font-medium hover:underline"
                >
                  Forgot password?
                </button>
              </div>
            )}

            {error && (
              <div data-testid="auth-error" className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
                {error}
              </div>
            )}

            <button
              data-testid={
                mode === "login" ? "login-submit-button"
                : mode === "register" ? "register-submit-button"
                : mode === "forgot" ? "send-reset-code-button"
                : "reset-password-button"
              }
              type="submit"
              disabled={loading}
              className="w-full h-12 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-400 text-black font-bold hover:opacity-90 transition-opacity flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              {mode === "login" ? "Sign In"
                : mode === "register" ? "Create Account"
                : mode === "forgot" ? "Send Reset Code"
                : "Update Password"}
            </button>
          </form>

          {(mode === "forgot" || mode === "reset") && (
            <button
              data-testid="back-to-login-link"
              onClick={() => { setMode("login"); setError(""); }}
              className="w-full h-11 mt-3 rounded-xl border border-[#33331f] text-white font-medium hover:bg-white/5 transition-colors"
            >
              Back to sign in
            </button>
          )}

          {mode === "reset" && (
            <p className="text-center text-white mt-4 text-sm">
              Didn't get a code?{" "}
              <button
                type="button"
                data-testid="resend-code-link"
                onClick={() => { setMode("forgot"); setError(""); }}
                className="text-emerald-400 font-semibold hover:underline"
              >
                Resend
              </button>
            </p>
          )}

          {(mode === "login" || mode === "register") && (
            <p className="text-center text-white mt-6 text-sm">
              {mode === "login" ? "Don't have an account? " : "Already have an account? "}
              <button
                data-testid="toggle-auth-mode"
                onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
                className="text-emerald-400 font-semibold hover:underline"
              >
                {mode === "login" ? "Sign up" : "Sign in"}
              </button>
            </p>
          )}
        </motion.div>
      </div>

      <style>{`
        .input-field {
          width: 100%; height: 3rem; padding: 0 1rem; border-radius: 0.75rem;
          background: #12141C; border: 1px solid rgba(30,41,59,0.9); color: #FFFFFF;
          outline: none; transition: border-color .2s, box-shadow .2s;
        }
        .input-field:focus { border-color: #34d399; box-shadow: 0 0 0 3px rgba(16,185,129,0.15); }
        .input-field::placeholder { color: rgba(233,230,4,0.4); }
      `}</style>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label className="block text-xs font-medium uppercase tracking-wider text-white mb-2">{label}</label>
    {children}
  </div>
);

const Feature = ({ icon, label }) => (
  <div className="flex flex-col items-center gap-2">
    <div className="w-11 h-11 rounded-xl bg-[#12141C] border border-[#242417] flex items-center justify-center">{icon}</div>
    <span className="text-xs text-white">{label}</span>
  </div>
);
